import { createFileRoute } from "@tanstack/react-router";
import Link from "@/components/link";
import { PageHeader } from "@/components/page-header";
import { Badge, type Tone } from "@/components/ui/badge";
import { DateRangeFilter } from "@/components/date-range-filter";
import { SkHeader, SkPanel } from "@/components/skeleton";
import { PageQuery } from "@/components/page-query";
import { storePresets } from "@shared/date-range";
import { fmtDateTime } from "@shared/format";
import { PERMISSION_LABELS, ROLE_LABELS, type Permission, type Role } from "@shared/permissions";
import type { AuditActor, AuditLogRow, AuditPage } from "@shared/pages/audit";
import { pageQuery } from "@/lib/queries";
import { useTitle } from "@/lib/title";

export const Route = createFileRoute("/_app/audit")({
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient.prefetchQuery(pageQuery<AuditPage>("audit", deps));
  },
  component: AuditRoute,
});

const KIND: Record<string, { label: string; tone: Tone }> = {
  staff: { label: "Staff", tone: "cyan" },
  role: { label: "Role", tone: "blue" },
  account: { label: "Account", tone: "slate" },
  products: { label: "Products", tone: "amber" },
};

const kindOf = (action: string) =>
  action === "permission_change"
    ? KIND.role
    : action.startsWith("product_alias")
      ? KIND.products
      : action.startsWith("password") || action === "staff_password_set" || action === "agency_login"
        ? KIND.account
        : KIND.staff;

const permLabel = (p: unknown) => PERMISSION_LABELS[p as Permission] ?? String(p);
const roleLabel = (r: unknown) => ROLE_LABELS[r as Role] ?? String(r);

function line(s: AuditLogRow): string {
  const who = s.target || "someone";
  const d = s.detail ?? {};
  switch (s.action) {
    case "staff_added":
      return `Added ${who} as ${roleLabel(d.role)}`;
    case "staff_role":
      return `${who}: role changed from ${roleLabel(d.from)} to ${roleLabel(d.to)}`;
    case "staff_active":
      return `${who} ${d.active ? "activated" : "deactivated"}`;
    case "staff_password_set":
      return `${who} set a new password`;
    case "staff_password_reset_sent":
      return `Password reset link sent to ${who}`;
    case "password_reset":
      return `${who} reset their password by email`;
    case "permission_change":
      return `${roleLabel(s.target)} role: ${permLabel(d.permission)} ${d.allowed ? "granted" : "revoked"}`;
    case "user_permission_change":
      return `${who}: ${permLabel(d.permission)} ${d.allowed ? "granted" : "revoked"} (just for them)`;
    case "user_permission_reset":
      return `${who}: back to role defaults`;
    case "product_alias_set":
      return `Matched ${who} to ${String(d.product ?? `Shopify product ${d.productId}`)}${d.variant ? ` (${String(d.variant)})` : ""}`;
    case "product_alias_removed":
      return `Removed the product match for ${who}`;
    case "agency_login":
      return `${who} signed in from the Digital King hub`;
    case "staff_removed":
      return `Removed ${who} from the team`;
    case "staff_profile_set":
      return `${who} updated their name or title`;
    default:
      return `${who}: ${s.action}`;
  }
}

function actorName(row: AuditLogRow, staffMap: Record<string, AuditActor>): string {
  const s = row.actorUserId ? staffMap[row.actorUserId] : undefined;
  return s?.full_name || s?.email || row.actorEmail || "System";
}

function buildHref(params: { from?: string; to?: string; page?: number }): string {
  const q = new URLSearchParams();
  if (params.from) q.set("from", params.from);
  if (params.to) q.set("to", params.to);
  if (params.page && params.page > 1) q.set("page", String(params.page));
  const s = q.toString();
  return s ? `/audit?${s}` : "/audit";
}

function Skeleton() {
  return (
    <div className="page-shell-narrow">
      <SkHeader withAction={false} />
      <SkPanel bodyClassName="h-40" />
      <SkPanel className="mt-6" bodyClassName="h-56" />
    </div>
  );
}

function AuditRoute() {
  useTitle("Audit log");
  const search = Route.useSearch();
  return (
    <PageQuery<AuditPage> name="audit" search={search} skeleton={<Skeleton />}>
      {(page) => <Audit page={page} />}
    </PageQuery>
  );
}

function Audit({ page: data }: { page: AuditPage }) {
  const { items, hasMore, from, to, page, actors } = data;
  const hasFilter = Boolean(from || to);

  return (
    <div className="page-shell-narrow">
      <PageHeader
        title="Audit log"
        info="Every staff, role and permission change: who, what and when (Eastern time)."
        actions={<DateRangeFilter presets={storePresets()} defaultKey="all" allowAllTime />}
      />

      <div className="rounded-xl border border-border bg-surface divide-y divide-border">
        {items.map((item) => {
          const kind = kindOf(item.action);
          return (
            <div key={item.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
              <div className="flex min-w-0 items-center gap-2.5">
                <Badge tone={kind.tone}>{kind.label}</Badge>
                <span className="truncate text-foreground">{line(item)}</span>
              </div>
              <span className="shrink-0 text-xs text-muted">
                {actorName(item, actors)} · {fmtDateTime(item.at)}
              </span>
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-muted">{hasFilter ? "No changes in this date range." : "No changes recorded yet."}</div>
        )}
      </div>

      {(page > 1 || hasMore) && (
        <div className="mt-4 flex items-center justify-between text-sm">
          {page > 1 ? (
            <Link href={buildHref({ from, to, page: page - 1 })} className="rounded-md border border-border px-3 py-1.5 font-semibold text-foreground hover:bg-surface-2">
              ← Previous
            </Link>
          ) : (
            <span className="rounded-md border border-border px-3 py-1.5 font-semibold text-muted opacity-50">← Previous</span>
          )}
          <span className="text-xs text-muted">Page {page}</span>
          {hasMore ? (
            <Link href={buildHref({ from, to, page: page + 1 })} className="rounded-md border border-border px-3 py-1.5 font-semibold text-foreground hover:bg-surface-2">
              Next →
            </Link>
          ) : (
            <span className="rounded-md border border-border px-3 py-1.5 font-semibold text-muted opacity-50">Next →</span>
          )}
        </div>
      )}
    </div>
  );
}
